// client/src/components/EnhancedPestWeatherAlerts.tsx
import React, { useState, useEffect } from 'react';
import { AlertTriangle, Bug, Cloud, Thermometer, Droplets, RefreshCw, Clock, MapPin } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

interface Alert {
  id: string;
  type: 'pest' | 'weather';
  severity: 'low' | 'medium' | 'high' | 'critical';
  title: string;
  titleSt: string;
  message: string;
  messageSt: string;
  location: string;
  timestamp: string;
  actionRequired: boolean;
}

interface AlertsData {
  alerts: Alert[];
  lastUpdated: string;
  source: string;
}

interface Props {
  userId?: string;
}

export default function EnhancedPestWeatherAlerts({ userId }: Props) {
  const { t, language } = useLanguage();
  const [alertsData, setAlertsData] = useState<AlertsData>({
    alerts: [],
    lastUpdated: new Date().toISOString(),
    source: 'Loading...'
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Load cached data first
    const cachedData = localStorage.getItem('cached_alerts');
    if (cachedData) {
      try {
        const parsed = JSON.parse(cachedData);
        setAlertsData(parsed);
      } catch (e) {
        console.error('Error parsing cached alerts:', e);
      }
    }
    
    // Then fetch fresh data
    fetchAlerts();
    
    // Listen for refresh events
    const handleRefresh = () => fetchAlerts();
    window.addEventListener('refreshAlerts', handleRefresh);
    
    return () => {
      window.removeEventListener('refreshAlerts', handleRefresh);
    };
  }, [userId]);

  const fetchAlerts = async () => {
    if (loading) return;
    
    try {
      setLoading(true);
      const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:5000';
      const url = userId 
        ? `${backendUrl}/api/pest-weather-alerts?userId=${userId}`
        : `${backendUrl}/api/pest-weather-alerts`;
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, 5000); // 5s timeout
      
      try {
        const response = await fetch(url, { 
          headers: { 'Content-Type': 'application/json' },
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        if (response.ok) {
          const data = await response.json();
          setAlertsData({
            ...data,
            lastUpdated: new Date().toISOString()
          });
          localStorage.setItem('cached_alerts', JSON.stringify({
            ...data,
            lastUpdated: new Date().toISOString()
          }));
        } else {
          throw new Error(`HTTP ${response.status}`);
        }
      } catch (fetchError: any) {
        clearTimeout(timeoutId);
        throw fetchError;
      }
    } catch (error: any) {
      console.log('Server unavailable, using offline data');
      
      // Generate offline fallback data
      const offlineData = {
        alerts: [
          {
            id: `offline-${Date.now()}`,
            type: 'pest' as const,
            severity: 'medium' as const,
            title: 'Seasonal Monitoring',
            titleSt: 'Tlhahlobo ea Nako',
            message: 'Continue monitoring crops for seasonal pest activity. Check plants regularly for signs of damage.',
            messageSt: 'Tsoela pele ho sheba lijalo bakeng sa likokoanyana tsa nako. Sheba limela kamehla bakeng sa matšoao a tšenyo.',
            location: 'Lesotho',
            timestamp: new Date().toISOString(),
            actionRequired: false
          }
        ],
        lastUpdated: new Date().toISOString(),
        source: 'Offline Mode'
      };
      
      setAlertsData(offlineData);
      localStorage.setItem('cached_alerts', JSON.stringify(offlineData));
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 border-red-300 text-red-800';
      case 'high': return 'bg-orange-100 border-orange-300 text-orange-800';
      case 'medium': return 'bg-yellow-100 border-yellow-300 text-yellow-800';
      default: return 'bg-blue-100 border-blue-300 text-blue-800';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical': return <AlertTriangle className="w-5 h-5 text-red-600" />;
      case 'high': return <AlertTriangle className="w-5 h-5 text-orange-600" />;
      case 'medium': return <AlertTriangle className="w-5 h-5 text-yellow-600" />;
      default: return <AlertTriangle className="w-5 h-5 text-blue-600" />;
    }
  };

  const getTypeIcon = (type: string) => {
    return type === 'pest' 
      ? <Bug className="w-4 h-4" />
      : <Cloud className="w-4 h-4" />;
  };

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && alertsData.alerts.length === 0) {
    return (
      <div className="text-center py-6">
        <RefreshCw className="w-6 h-6 mx-auto mb-2 text-blue-600 animate-spin" />
        <p className="text-gray-600">
          {language === 'st' ? 'Ho laela litemoso...' : 'Loading alerts...'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">
            {language === 'st' ? 'Litemoso tsa Hajoale' : 'Active Alerts'}
          </span>
          <span className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded-full">
            {alertsData.alerts.length}
          </span>
        </div>
        <button
          onClick={fetchAlerts}
          disabled={loading}
          className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:text-gray-800"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          {language === 'st' ? 'Nchafatsa' : 'Refresh'}
        </button>
      </div>

      <div className="space-y-2 max-h-64 overflow-y-auto">
        {alertsData.alerts.map((alert) => (
          <div
            key={alert.id}
            className={`p-3 rounded-lg border ${getSeverityColor(alert.severity)}`}
          >
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                {getSeverityIcon(alert.severity)}
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  {getTypeIcon(alert.type)}
                  <h4 className="font-semibold text-sm">
                    {language === 'st' ? alert.titleSt : alert.title}
                  </h4>
                </div>
                
                <p className="text-sm mb-2">
                  {language === 'st' ? alert.messageSt : alert.message}
                </p>
                
                <div className="flex items-center justify-between text-xs text-gray-600">
                  <div className="flex items-center gap-3">
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {alert.location}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatTime(alert.timestamp)}
                    </span>
                    {(alert as any).isTrusted && (
                      <span className="px-1.5 py-0.5 bg-green-200 text-green-800 rounded text-xs font-medium">
                        ✓ Verified
                      </span>
                    )}
                  </div>
                  
                  {alert.actionRequired && (
                    <span className="px-2 py-0.5 bg-red-200 text-red-800 rounded text-xs font-medium">
                      {language === 'st' ? 'Hloka Ketso' : 'Action Required'}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {alertsData.source && (
        <div className="text-xs text-gray-500 text-center pt-2 border-t">
          <div>{language === 'st' ? 'Mohloli' : 'Source'}: {alertsData.source}</div>
          {(alertsData as any).trustedSources > 0 && (
            <div className="text-green-600 font-medium mt-1">
              ✓ {(alertsData as any).trustedSources} from trusted sources
            </div>
          )}
        </div>
      )}
    </div>
  );
}